import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Container } from './ui/Container'
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card'
import { Button } from './ui/Button'
import { toast } from 'sonner'
import {
  Dumbbell,
  Heart,
  Zap,
  Target,
  TrendingUp,
  Clock,
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  CheckCircle,
  Flame,
  Activity,
  Timer,
  Award,
  Plus,
  Calendar,
  Info
} from 'lucide-react'

interface Exercise {
  id: string
  name: string
  category: 'strength' | 'cardio' | 'flexibility' | 'balance' | 'core'
  duration: number
  sets?: number
  reps?: number
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  equipment: string[]
  muscles: string[]
  instructions: string[]
  benefits: string[]
  calories: number
  image: string
  video?: string
}

interface WorkoutPlan {
  id: string
  name: string
  description: string
  duration: number
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  focus: 'strength' | 'cardio' | 'flexibility' | 'weight-loss' | 'muscle-gain' | 'endurance'
  exercises: string[]
  estimatedCalories: number
  equipment: string[]
  image: string
  benefits: string[]
  weeklyFrequency: number
}

interface WorkoutSession {
  exerciseId: string
  completed: boolean
  actualSets?: number
  actualReps?: number
  actualDuration?: number
  caloriesBurned?: number
}

const exercises: Exercise[] = [
  {
    id: '1',
    name: 'Push-ups',
    category: 'strength',
    duration: 60,
    sets: 3,
    reps: 12,
    difficulty: 'beginner',
    equipment: ['none'],
    muscles: ['chest', 'shoulders', 'triceps'],
    instructions: ['Start in plank position', 'Lower body to ground', 'Push back up', 'Keep core engaged'],
    benefits: ['Upper body strength', 'Core stability', 'No equipment needed'],
    calories: 8,
    image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: '2',
    name: 'Jumping Jacks',
    category: 'cardio',
    duration: 60,
    difficulty: 'beginner',
    equipment: ['none'],
    muscles: ['full-body', 'cardiovascular'],
    instructions: ['Stand with feet together', 'Jump while spreading legs', 'Raise arms overhead', 'Return to starting position'],
    benefits: ['Cardiovascular health', 'Full-body coordination', 'Calorie burning'],
    calories: 10,
    image: 'https://images.unsplash.com/photo-1538805394723-43d527df499b?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: '3',
    name: 'Squats',
    category: 'strength',
    duration: 60,
    sets: 3,
    reps: 15,
    difficulty: 'beginner',
    equipment: ['none'],
    muscles: ['quadriceps', 'glutes', 'hamstrings'],
    instructions: ['Stand with feet shoulder-width apart', 'Lower hips back and down', 'Keep chest up', 'Return to standing'],
    benefits: ['Lower body strength', 'Functional movement', 'Core engagement'],
    calories: 9,
    image: 'https://images.unsplash.com/photo-1574680096145-d05b474e2155?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: '4',
    name: 'Plank',
    category: 'core',
    duration: 45,
    difficulty: 'intermediate',
    equipment: ['none'],
    muscles: ['core', 'shoulders', 'back'],
    instructions: ['Start in push-up position', 'Lower to forearms', 'Keep body straight', 'Hold position'],
    benefits: ['Core strength', 'Posture improvement', 'Full-body stability'],
    calories: 7,
    image: 'https://images.unsplash.com/photo-1566241440091-ec10de8db2e1?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: '5',
    name: 'Tree Pose (Vrikshasana)',
    category: 'balance',
    duration: 60,
    difficulty: 'beginner',
    equipment: ['none'],
    muscles: ['legs', 'core', 'ankles'],
    instructions: ['Stand tall', 'Place one foot on inner thigh', 'Bring hands to heart center', 'Focus on a single point'],
    benefits: ['Improves balance', 'Strengthens legs', 'Focus and concentration'],
    calories: 3,
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: '6',
    name: 'Warrior II (Virabhadrasana II)',
    category: 'strength',
    duration: 60,
    difficulty: 'beginner',
    equipment: ['none'],
    muscles: ['quads', 'shoulders', 'arms'],
    instructions: ['Step feet wide apart', 'Turn one foot out 90 degrees', 'Bend front knee', 'Extend arms parallel to floor'],
    benefits: ['Full body engagement', 'Stamina building', 'Hip opening'],
    calories: 5,
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: '7',
    name: 'Downward Dog (Adho Mukha Svanasana)',
    category: 'flexibility',
    duration: 60,
    difficulty: 'beginner',
    equipment: ['none'],
    muscles: ['hamstrings', 'shoulders', 'back'],
    instructions: ['Start on hands and knees', 'Lift hips to ceiling', 'Press heels toward floor', 'Push through palms'],
    benefits: ['Total body stretch', 'Upper body strength', 'Back health'],
    calories: 4,
    image: 'https://images.unsplash.com/photo-1588280991492-4914101ea333?auto=format&fit=crop&q=80&w=800'
  }
]

const workoutPlans: WorkoutPlan[] = [
  {
    id: '1',
    name: 'Beginner Full Body',
    description: 'Perfect introduction to strength training with basic exercises',
    duration: 20,
    difficulty: 'beginner',
    focus: 'strength',
    exercises: ['1', '2', '3', '4'],
    estimatedCalories: 120,
    equipment: ['none'],
    image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&q=80&w=800',
    benefits: ['Builds foundation', 'Improves form', 'Full-body workout', 'No equipment needed'],
    weeklyFrequency: 3
  },
  {
    id: '2',
    name: 'HIIT Cardio Blast',
    description: 'High-intensity interval training for maximum calorie burn',
    duration: 15,
    difficulty: 'intermediate',
    focus: 'cardio',
    exercises: ['2', '3'],
    estimatedCalories: 180,
    equipment: ['none'],
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&q=80&w=800',
    benefits: ['Maximum calorie burn', 'Improves endurance', 'Time-efficient', 'Boosts metabolism'],
    weeklyFrequency: 4
  },
  {
    id: '3',
    name: 'Mindful Flow Yoga',
    description: 'A calming sequence to improve balance, flexibility and focus',
    duration: 18,
    difficulty: 'beginner',
    focus: 'flexibility',
    exercises: ['5', '6', '7'],
    estimatedCalories: 85,
    equipment: ['none'],
    image: 'https://images.unsplash.com/photo-1508670666349-f56734563cd0?auto=format&fit=crop&q=80&w=800',
    benefits: ['Balance & Focus', 'Better flexibility', 'Stress reduction', 'Joint health'],
    weeklyFrequency: 4
  }
]


const WorkoutSection: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<WorkoutPlan | null>(null)
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [completedExercises, setCompletedExercises] = useState<WorkoutSession[]>([])
  const [totalCaloriesBurned, setTotalCaloriesBurned] = useState(0)
  const [sessionDuration, setSessionDuration] = useState(0)
  const [showExerciseDetail, setShowExerciseDetail] = useState<Exercise | null>(null)

  const handleComingSoon = (feature: string) => {
    toast.info(`🚀 ${feature} - Feature Coming Soon!`, {
      duration: 3000,
      position: 'top-center',
    });
  };

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (isPlaying && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleExerciseComplete()
            return 0
          }
          return prev - 1
        })
        setSessionDuration(prev => prev + 1)
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isPlaying, timeRemaining])

  const handleStartWorkout = (plan: WorkoutPlan) => {
    setSelectedPlan(plan)
    setCurrentExerciseIndex(0)
    setCompletedExercises([])
    setTotalCaloriesBurned(0)
    setSessionDuration(0)
    const firstExercise = exercises.find(ex => ex.id === plan.exercises[0])
    if (firstExercise) {
      setTimeRemaining(firstExercise.duration)
    }
  }

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const handleExerciseComplete = () => {
    if (!selectedPlan) return

    const currentExercise = exercises.find(ex => ex.id === selectedPlan.exercises[currentExerciseIndex])
    if (currentExercise) {
      const caloriesBurned = (currentExercise.calories * currentExercise.duration) / 60
      setTotalCaloriesBurned(prev => prev + caloriesBurned)

      const completedExercise: WorkoutSession = {
        exerciseId: currentExercise.id,
        completed: true,
        actualDuration: currentExercise.duration,
        caloriesBurned: caloriesBurned
      }

      setCompletedExercises(prev => [...prev, completedExercise])
    }

    if (currentExerciseIndex < selectedPlan.exercises.length - 1) {
      const nextExerciseIndex = currentExerciseIndex + 1
      setCurrentExerciseIndex(nextExerciseIndex)
      const nextExercise = exercises.find(ex => ex.id === selectedPlan.exercises[nextExerciseIndex])
      if (nextExercise) {
        setTimeRemaining(nextExercise.duration)
      }
    } else {
      // Workout complete
      setIsPlaying(false)
    }
  }

  const handleReset = () => {
    setCurrentExerciseIndex(0)
    setIsPlaying(false)
    setCompletedExercises([])
    setTotalCaloriesBurned(0)
    setSessionDuration(0)
    if (selectedPlan) {
      const firstExercise = exercises.find(ex => ex.id === selectedPlan.exercises[0])
      if (firstExercise) {
        setTimeRemaining(firstExercise.duration)
      }
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getDifficultyColor = (difficulty: string) => {
    const colors = {
      beginner: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      advanced: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    }
    return colors[difficulty as keyof typeof colors]
  }

  const getCategoryIcon = (category: string) => {
    const icons = {
      strength: <Dumbbell className="w-5 h-5" />,
      cardio: <Heart className="w-5 h-5" />,
      flexibility: <Activity className="w-5 h-5" />,
      balance: <Target className="w-5 h-5" />,
      core: <Zap className="w-5 h-5" />
    }
    return icons[category as keyof typeof icons] || <Dumbbell className="w-5 h-5" />
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      strength: 'from-red-500 to-pink-600',
      cardio: 'from-orange-500 to-red-600',
      flexibility: 'from-green-500 to-emerald-600',
      balance: 'from-blue-500 to-cyan-600',
      core: 'from-purple-500 to-indigo-600'
    }
    return colors[category as keyof typeof colors] || 'from-gray-500 to-gray-600'
  }

  const currentExercise = selectedPlan ? exercises.find(ex => ex.id === selectedPlan.exercises[currentExerciseIndex]) : null
  const workoutProgress = selectedPlan ? (completedExercises.length / selectedPlan.exercises.length) * 100 : 0

  return (
    <section className="py-20 bg-theme-bg content-section">
      <Container>
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl mb-6">
              <Dumbbell className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-6">
              AI-Powered Workout Plans
            </h2>
            <p className="text-xl text-theme-secondary max-w-3xl mx-auto leading-relaxed">
              Get personalized workout routines based on your fitness level, goals, and available equipment.
              Track your progress with real-time metrics and adaptive recommendations.
            </p>
          </motion.div>
        </div>

        {/* Workout Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent gradient-heading mb-8 text-center">Recommended Workout Plans</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workoutPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full flex flex-col hover:shadow-lg transition-all duration-300 cursor-pointer bg-theme-surface/70 border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 rounded-xl group">
                  <CardHeader className="pb-4">
                    <div className="mb-4">
                      <img
                        src={plan.image}
                        alt={plan.name}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-lg text-theme-primary group-hover:text-blue-400">{plan.name}</CardTitle>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(plan.difficulty)}`}>
                        {plan.difficulty}
                      </div>
                    </div>
                    <p className="text-sm text-theme-secondary group-hover:text-blue-400 mb-3">{plan.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-theme-secondary group-hover:text-blue-400">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{plan.duration} min</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Flame className="w-4 h-4" />
                        <span>{plan.estimatedCalories} cal</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{plan.weeklyFrequency}x/week</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col">
                    <div className="space-y-2 mb-4">
                      {plan.benefits.slice(0, 3).map((benefit, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span className="text-theme-secondary">{benefit}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex space-x-2 mt-auto">
                      <Button
                        size="sm"
                        className="flex-1 bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-700"
                        onClick={() => handleStartWorkout(plan)}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Workout
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowExerciseDetail(exercises.find(ex => plan.exercises.includes(ex.id)) || null)}
                      >
                        <Info className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Workout Player */}
        <AnimatePresence>
          {selectedPlan && currentExercise && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="bg-theme-surface/90 rounded-2xl p-8 border border-blue-200 dark:border-blue-700 shadow-xl mb-8"
            >
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-theme-primary mb-2">{selectedPlan.name}</h3>
                <div className="flex items-center justify-center space-x-6 text-sm text-theme-secondary">
                  <div className="flex items-center space-x-1">
                    <Timer className="w-4 h-4" />
                    <span>{formatTime(timeRemaining)} remaining</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Target className="w-4 h-4" />
                    <span>Exercise {currentExerciseIndex + 1} of {selectedPlan.exercises.length}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Flame className="w-4 h-4" />
                    <span>{Math.round(totalCaloriesBurned)} cal burned</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(sessionDuration)} total</span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Current Exercise */}
                <div>
                  <div className="mb-4">
                    <img
                      src={currentExercise.image}
                      alt={currentExercise.name}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                  <div className="text-center mb-4">
                    <h4 className="text-xl font-bold text-theme-primary">{currentExercise.name}</h4>
                    <div className="flex items-center justify-center space-x-2 text-sm text-theme-secondary">
                      <div className={`w-8 h-8 bg-gradient-to-br ${getCategoryColor(currentExercise.category)} rounded-lg flex items-center justify-center text-white`}>
                        {getCategoryIcon(currentExercise.category)}
                      </div>
                      <span className="capitalize">{currentExercise.category}</span>
                      <span className="text-theme-text-muted">•</span>
                      <span className="capitalize">{currentExercise.difficulty}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <h5 className="font-semibold text-theme-primary mb-2">Instructions</h5>
                      <ol className="space-y-1">
                        {currentExercise.instructions.map((instruction, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-sm text-theme-secondary">
                            <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mt-0.5">
                              {idx + 1}
                            </span>
                            <span>{instruction}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Exercise Details & Controls */}
                <div>
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                        <div className="text-lg font-bold text-theme-primary">
                          {currentExercise.sets ? `${currentExercise.sets} × ${currentExercise.reps}` : `${currentExercise.duration}s`}
                        </div>
                        <div className="text-xs text-theme-secondary">
                          {currentExercise.sets ? 'sets × reps' : 'duration'}
                        </div>
                      </div>
                      <div className="text-center p-3 bg-theme-surface/60 rounded-lg">
                        <div className="text-lg font-bold text-theme-primary">{currentExercise.calories}</div>
                        <div className="text-xs text-theme-secondary">cal/min</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold text-theme-primary mb-2 flex items-center">
                        <Heart className="w-4 h-4 mr-2 text-red-500" />
                        Target Muscles
                      </h5>
                      <div className="flex flex-wrap gap-1">
                        {currentExercise.muscles.map((muscle, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-500/20 text-blue-800 dark:text-blue-200 text-xs rounded-full border border-blue-300 dark:border-blue-600">
                            {muscle}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold text-theme-primary mb-2 flex items-center">
                        <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
                        Benefits
                      </h5>
                      <ul className="space-y-1">
                        {currentExercise.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center space-x-2 text-sm text-theme-secondary">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-4">
                      <Button
                        size="lg"
                        onClick={handlePlayPause}
                        className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-700"
                      >
                        {isPlaying ? (
                          <>
                            <Pause className="w-5 h-5" />
                            <span>Pause</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-5 h-5" />
                            <span>Play</span>
                          </>
                        )}
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={handleReset}
                        className="flex items-center space-x-2"
                      >
                        <RotateCcw className="w-5 h-5" />
                        <span>Reset</span>
                      </Button>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-theme-secondary">Workout Progress</span>
                        <span className="font-semibold text-theme-primary">
                          {Math.round(workoutProgress)}%
                        </span>
                      </div>
                      <div className="w-full bg-theme-surface/60 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-teal-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${workoutProgress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Exercise Library */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-theme-primary mb-8 text-center">Exercise Library</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {exercises.map((exercise, index) => (
              <motion.div
                key={exercise.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 cursor-pointer bg-theme-surface/70 border border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 rounded-xl group">
                  <CardHeader className="pb-3">
                    <div className="mb-3">
                      <img
                        src={exercise.image}
                        alt={exercise.name}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className="text-base text-theme-primary group-hover:text-blue-400">{exercise.name}</CardTitle>
                      <div className={`w-8 h-8 bg-gradient-to-br ${getCategoryColor(exercise.category)} rounded-lg flex items-center justify-center text-white`}>
                        {getCategoryIcon(exercise.category)}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-theme-secondary group-hover:text-blue-400">
                      <span className={`px-2 py-1 rounded-full ${getDifficultyColor(exercise.difficulty)}`}>
                        {exercise.difficulty}
                      </span>
                      <span>{exercise.duration}s</span>
                      <span>{exercise.calories} cal/min</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div>
                        <h5 className="font-semibold text-theme-primary group-hover:text-blue-400 text-sm mb-1">Muscles</h5>
                        <div className="flex flex-wrap gap-1">
                          {exercise.muscles.slice(0, 2).map((muscle, idx) => (
                            <span key={idx} className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded border border-blue-200 dark:border-blue-600">
                              {muscle}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h5 className="font-semibold text-theme-primary group-hover:text-blue-400 text-sm mb-1">Equipment</h5>
                        <div className="text-xs text-theme-secondary">
                          {exercise.equipment.join(', ')}
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="w-full mt-3"
                      onClick={() => setShowExerciseDetail(exercise)}
                    >
                      View Details
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Exercise Detail Modal */}
        {showExerciseDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowExerciseDetail(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-theme-surface/90 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-blue-200 dark:border-blue-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 bg-gradient-to-br from-blue-600 to-teal-500 rounded-xl flex items-center justify-center text-white`}>
                    {getCategoryIcon(showExerciseDetail.category)}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-theme-primary">{showExerciseDetail.name}</h3>
                    <div className="flex items-center space-x-2 text-sm text-theme-secondary">
                      <span className={`px-2 py-1 rounded-full ${getDifficultyColor(showExerciseDetail.difficulty)}`}>
                        {showExerciseDetail.difficulty}
                      </span>
                      <span>{showExerciseDetail.duration}s</span>
                      <span>{showExerciseDetail.calories} cal/min</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowExerciseDetail(null)}
                  className="text-theme-secondary hover:text-theme-text text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <img
                    src={showExerciseDetail.image}
                    alt={showExerciseDetail.name}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>

                <div>
                  <h4 className="font-semibold text-theme-primary mb-2">Instructions</h4>
                  <ol className="space-y-2">
                    {showExerciseDetail.instructions.map((instruction, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm text-theme-secondary">
                        <span className="bg-primary-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mt-0.5">
                          {idx + 1}
                        </span>
                        <span>{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                      <Heart className="w-4 h-4 mr-2 text-red-500" />
                      Target Muscles
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {showExerciseDetail.muscles.map((muscle, idx) => (
                        <span key={idx} className="px-2 py-1 bg-red-500/20 text-red-800 dark:text-red-200 text-xs rounded-full border border-red-300 dark:border-red-600">
                          {muscle}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-theme-primary mb-2 flex items-center">
                      <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
                      Benefits
                    </h4>
                    <ul className="space-y-1">
                      {showExerciseDetail.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-sm text-theme-secondary">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <Button className="flex-1">
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Custom Workout
                  </Button>
                  <Button variant="outline" className="flex-1">
                    <Award className="w-4 h-4 mr-2" />
                    Track Progress
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center bg-theme-surface/80 border-2 border-blue-200 dark:border-blue-700 rounded-2xl p-8 text-theme-text"
        >
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-teal-500 rounded-2xl flex items-center justify-center">
            <Dumbbell className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-3xl font-bold mb-4 text-theme-primary">Transform Your Fitness Journey</h3>
          <p className="text-xl mb-8 text-theme-secondary max-w-2xl mx-auto">
            Get AI-powered workout recommendations based on your fitness level, goals, and available equipment.
            Track your progress and achieve your fitness objectives with personalized guidance.
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-teal-500 text-white hover:from-blue-700 hover:to-teal-600"
            onClick={() => handleComingSoon('Workout Plan')}
          >
            Create My Workout Plan
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </Container>
    </section>
  )
}

export default WorkoutSection
